import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ClientLayout from "@/components/ClientLayout";
import { useCart } from "@/context/CartContext";
import { useRestaurant } from "@/context/RestaurantContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { History, Repeat, Eye, Package, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pendiente", className: "status-pending" },
  confirmed: { label: "Confirmado", className: "status-confirmed" },
  processing: { label: "Procesando", className: "status-processing" },
  shipped: { label: "Enviado", className: "status-shipped" },
  delivered: { label: "Entregado", className: "status-delivered" },
  cancelled: { label: "Cancelado", className: "status-cancelled" },
};

export default function ClientHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { setCartFromOrder } = useCart();
  const { selected: restaurant } = useRestaurant();
  const navigate = useNavigate();
  const canSeePrices = restaurant?.show_prices_to_client !== false;

  useEffect(() => {
    if (!restaurant) {
      setOrders([]);
      setLoading(false);
      return;
    }
    fetchOrders();
  }, [restaurant?.restaurant_id]);

  const fetchOrders = async () => {
    if (!restaurant) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/orders`, {
        params: { restaurant_id: restaurant.restaurant_id },
      });
      setOrders(response.data);
    } catch (error) {
      toast.error("Error al cargar el historial");
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = (order) => {
    setCartFromOrder(order.items, restaurant?.restaurant_id || null);
    toast.success("Productos añadidos al carrito");
    navigate("/dashboard");
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="order-history">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <History className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Historial de Pedidos
            </h1>
            <p className="text-slate-500">
              Revisa tus pedidos anteriores y repite fácilmente
            </p>
            {restaurant && (
              <p className="text-sm text-blue-700 mt-2" data-testid="history-current-restaurant">
                Mostrando pedidos de {restaurant.name}
              </p>
            )}
          </div>
        </div>

        {/* Orders list */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              No tienes pedidos aún
            </h2>
            <p className="text-slate-500 mb-6">
              Realiza tu primer pedido desde el catálogo
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="go-to-dashboard-btn"
            >
              Ir al catálogo
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const status = statusConfig[order.status] || statusConfig.pending;
              
              return (
                <Card
                  key={order.order_id}
                  className={`stagger-item bg-white shadow-sm hover:shadow-md transition-shadow`}
                  data-testid={`order-card-${order.order_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-900">
                            Pedido #{order.order_id.substring(0, 8)}
                          </h3>
                          <Badge className={status.className}>
                            {status.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.created_at)}
                        </div>
                        <p className="text-sm text-slate-600">
                          {order.items.length} producto{order.items.length !== 1 ? "s" : ""}
                          {canSeePrices && (
                            <span className="font-semibold text-slate-900 ml-1">
                              · {order.total.toFixed(2)}€
                            </span>
                          )}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setSelectedOrder(order)}
                          className="gap-2"
                          data-testid={`view-order-${order.order_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </Button>
                        <Button
                          onClick={() => handleReorder(order)}
                          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                          data-testid={`reorder-${order.order_id}`}
                        >
                          <Repeat className="w-4 h-4" />
                          Repetir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Order detail dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Pedido #{selectedOrder?.order_id.substring(0, 8)}
              </DialogTitle>
              <DialogDescription>
                Detalle del pedido para {restaurant?.name || "el restaurante seleccionado"}.
              </DialogDescription>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Fecha</span>
                  <span className="font-medium">
                    {formatDate(selectedOrder.created_at)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Estado</span>
                  <Badge className={statusConfig[selectedOrder.status]?.className}>
                    {statusConfig[selectedOrder.status]?.label}
                  </Badge>
                </div>
                
                <Separator />
                
                <div>
                  <h4 className="font-medium mb-3">Productos</h4>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            {item.product_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {item.quantity} {item.unit}
                          </p>
                        </div>
                        {canSeePrices && (
                          <span className="font-semibold">
                            {(item.quantity * item.price).toFixed(2)}€
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedOrder.notes && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">Notas</h4>
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                        {selectedOrder.notes}
                      </p>
                    </div>
                  </>
                )}
                
                {canSeePrices && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between text-lg font-semibold">
                      <span>Total</span>
                      <span className="text-blue-600">
                        {selectedOrder.total.toFixed(2)}€
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 text-right">* Sin IVA</p>
                  </>
                )}
                
                <Button
                  onClick={() => {
                    handleReorder(selectedOrder);
                    setSelectedOrder(null);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  data-testid="dialog-reorder-btn"
                >
                  <Repeat className="w-4 h-4" />
                  Repetir este pedido
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
}
